import type { ChargePresentation } from '../../../api/presentation.js';
import type { PorteeFile } from '../../stand/fileHorsLigne.js';

export interface PreparationPresentation extends PorteeFile {
  readonly selectionId: string;
  readonly prepareeLe: string;
  readonly expireLe: string;
  readonly octets: number;
}
export interface PresentationPreparee extends PreparationPresentation {
  readonly charge: ChargePresentation;
  readonly images: ReadonlyMap<string, Blob>;
}
export interface MagasinPresentation {
  preparer(
    portee: PorteeFile,
    charge: ChargePresentation,
    images: ReadonlyMap<string, Blob>,
  ): Promise<PreparationPresentation>;
  lire(portee: PorteeFile, selectionId: string): Promise<PresentationPreparee | null>;
  lister(portee: PorteeFile): Promise<PreparationPresentation[]>;
  effacer(portee: PorteeFile, selectionId: string): Promise<void>;
  effacerPortee(portee: PorteeFile): Promise<void>;
  toutEffacer(): Promise<void>;
}
export class PresentationExpiree extends Error {
  constructor() {
    super('La préparation hors ligne a expiré.');
  }
}
export interface StockagePresentation {
  ecrire(id: string, preparation: PresentationPreparee): Promise<void>;
  lire(id: string): Promise<PresentationPreparee | null>;
  identifiants(): Promise<string[]>;
  effacer(id: string): Promise<void>;
}
export const prefixePortee = (portee: PorteeFile): string =>
  `${portee.compteId}:${portee.galerieSlug}:`;

export function creerMagasinPresentation(
  stockage: StockagePresentation,
  maintenant = () => new Date(),
): MagasinPresentation {
  const identifiant = (portee: PorteeFile, selectionId: string) =>
    `${prefixePortee(portee)}${selectionId}`;
  async function lire(id: string) {
    const preparation = await stockage.lire(id);
    if (preparation && Date.parse(preparation.expireLe) <= maintenant().getTime()) {
      await stockage.effacer(id);
      throw new PresentationExpiree();
    }
    return preparation;
  }
  async function effacerPrefixe(prefixe: string) {
    for (const id of await stockage.identifiants())
      if (id.startsWith(prefixe)) await stockage.effacer(id);
  }
  return {
    async preparer(portee, charge, images) {
      const date = maintenant();
      const preparation: PresentationPreparee = {
        ...portee,
        selectionId: charge.selection.id,
        prepareeLe: date.toISOString(),
        expireLe: new Date(date.getTime() + 7 * 86400000).toISOString(),
        octets:
          new TextEncoder().encode(JSON.stringify(charge)).byteLength +
          [...images.values()].reduce((total, image) => total + image.size, 0),
        charge,
        images,
      };
      await stockage.ecrire(identifiant(portee, charge.selection.id), preparation);
      return preparation;
    },
    lire: (portee, selectionId) => lire(identifiant(portee, selectionId)),
    async lister(portee) {
      const preparations: PreparationPresentation[] = [];
      for (const id of await stockage.identifiants()) {
        if (!id.startsWith(prefixePortee(portee))) continue;
        try {
          const preparation = await lire(id);
          if (preparation) preparations.push(preparation);
        } catch (erreur) {
          if (!(erreur instanceof PresentationExpiree)) throw erreur;
        }
      }
      return preparations;
    },
    effacer: (portee, selectionId) => stockage.effacer(identifiant(portee, selectionId)),
    effacerPortee: (portee) => effacerPrefixe(prefixePortee(portee)),
    toutEffacer: () => effacerPrefixe(''),
  };
}

export function magasinPresentationMemoire(maintenant = () => new Date()): MagasinPresentation {
  const preparations = new Map<string, PresentationPreparee>();
  return creerMagasinPresentation(
    {
      ecrire: async (id, preparation) => {
        preparations.set(id, structuredClone(preparation));
      },
      lire: async (id) => preparations.get(id) ?? null,
      identifiants: async () => [...preparations.keys()],
      effacer: async (id) => {
        preparations.delete(id);
      },
    },
    maintenant,
  );
}
